import { useEffect, useState, useCallback } from "react";
import { useI18n } from "@/i18n/I18nContext";
import { pickLocalized } from "@/i18n/translations";
import api from "@/lib/api";
import Ornament from "@/components/Ornament";

const HERO = "/photos/prieure-meadow.jpg";
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const resolveAsset = (u) => (!u ? "" : u.startsWith("http") ? u : `${BACKEND}${u}`);

const PAGE_SIZE = 24;

// Visual + textual identity of the page — kept in French in all 11 languages
// (user choice, see iter26). Subtitles + UI chrome remain locally translated.
const TITLE = "le Prieuré s'éveille…";

const COPY = {
  fr: { kicker: "Mémoire en images et en mouvement", intro: "Les saisons du prieuré, les visages des Amis, les heures lentes de la baie.", emptyPhotos: "Bientôt les premières photographies du prieuré.", emptyVideos: "Bientôt les premières vidéos du prieuré.", loadMore: "↓ Charger plus", close: "Fermer", tabPhotos: "Photographies", tabVideos: "Vidéos" },
  en: { kicker: "Memory in pictures and motion", intro: "The priory's seasons, the faces of its Friends, the slow hours of the bay.", emptyPhotos: "The first photographs of the priory are coming soon.", emptyVideos: "The first videos of the priory are coming soon.", loadMore: "↓ Load more", close: "Close", tabPhotos: "Photographs", tabVideos: "Videos" },
  it: { kicker: "Memoria in immagini e movimento", intro: "Le stagioni del priorato, i volti degli Amici, le ore lente della baia.", emptyPhotos: "Le prime fotografie del priorato arriveranno presto.", emptyVideos: "I primi video del priorato arriveranno presto.", loadMore: "↓ Carica altro", close: "Chiudi", tabPhotos: "Fotografie", tabVideos: "Video" },
  es: { kicker: "Memoria en imágenes y movimiento", intro: "Las estaciones del priorato, los rostros de los Amigos, las horas lentas de la bahía.", emptyPhotos: "Las primeras fotografías del priorato llegarán pronto.", emptyVideos: "Los primeros vídeos del priorato llegarán pronto.", loadMore: "↓ Cargar más", close: "Cerrar", tabPhotos: "Fotografías", tabVideos: "Vídeos" },
  de: { kicker: "Erinnerung in Bildern und Bewegung", intro: "Die Jahreszeiten des Priorats, die Gesichter der Freunde, die langsamen Stunden der Bucht.", emptyPhotos: "Die ersten Fotografien des Priorats folgen bald.", emptyVideos: "Die ersten Videos des Priorats folgen bald.", loadMore: "↓ Mehr laden", close: "Schließen", tabPhotos: "Fotografien", tabVideos: "Videos" },
};

export default function Galerie() {
  const { locale } = useI18n();
  const t = COPY[locale] || COPY.fr;

  const [tab, setTab] = useState("photos"); // "photos" | "videos"
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loadingP, setLoadingP] = useState(true);
  const [loadingV, setLoadingV] = useState(true);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Lightbox = { kind: "photo"|"video", index: number }
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    let mounted = true;
    api.get("/photos", { params: { on_gallery: true, limit: 500 } })
      .then((r) => mounted && setPhotos(r.data.items || []))
      .finally(() => mounted && setLoadingP(false));
    api.get("/videos", { params: { on_gallery: true, limit: 200 } })
      .then((r) => mounted && setVideos(r.data.items || []))
      .finally(() => mounted && setLoadingV(false));
    return () => { mounted = false; };
  }, []);

  // Reset paging when switching tabs
  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [tab]);

  const currentList = tab === "photos" ? photos : videos;
  const visible = currentList.slice(0, visibleCount);
  const hasMore = currentList.length > visibleCount;

  // Keyboard navigation in the lightbox
  useEffect(() => {
    if (lightbox === null) return undefined;
    const handler = (e) => {
      if (e.key === "Escape") setLightbox(null);
      const list = lightbox.kind === "photo" ? photos : videos;
      if (e.key === "ArrowRight") setLightbox((s) => (s == null ? s : { kind: s.kind, index: Math.min(list.length - 1, s.index + 1) }));
      if (e.key === "ArrowLeft") setLightbox((s) => (s == null ? s : { kind: s.kind, index: Math.max(0, s.index - 1) }));
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [lightbox, photos, videos]);

  useEffect(() => {
    document.body.style.overflow = lightbox !== null ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [lightbox]);

  const close = useCallback(() => setLightbox(null), []);
  const openPhoto = (i) => setLightbox({ kind: "photo", index: i });
  const openVideo = (i) => setLightbox({ kind: "video", index: i });

  return (
    <div data-testid="galerie-page">
      <section className="relative" style={{ height: "42vh", minHeight: 320 }}>
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-vignette" />
        <div className="container-prieure relative h-full flex flex-col justify-end pb-12">
          <div className="font-caps text-[0.72rem] text-white/90 mb-4" style={{ letterSpacing: "0.28em" }}>
            {t.kicker}
          </div>
          <h1 className="font-display text-5xl sm:text-7xl text-white" data-testid="galerie-title">{TITLE}</h1>
        </div>
      </section>

      <section className="py-16">
        <div className="container-prieure max-w-3xl">
          <Ornament className="mb-6" />
          <p className="font-display italic text-2xl text-center" style={{ color: "var(--ink-2)" }}>
            {t.intro}
          </p>
        </div>
      </section>

      {/* Tabs Photos / Vidéos */}
      <div className="container-prieure flex justify-center gap-3 mb-10" data-testid="galerie-tabs">
        <TabButton active={tab === "photos"} onClick={() => setTab("photos")} testid="galerie-tab-photos">
          {t.tabPhotos} <span style={{ color: "var(--muted)" }}>({photos.length})</span>
        </TabButton>
        <TabButton active={tab === "videos"} onClick={() => setTab("videos")} testid="galerie-tab-videos">
          {t.tabVideos} <span style={{ color: "var(--muted)" }}>({videos.length})</span>
        </TabButton>
      </div>

      <section className="pb-32">
        <div className="container-prieure">
          {(tab === "photos" && loadingP) || (tab === "videos" && loadingV) ? (
            <div className="text-center font-display italic text-xl" style={{ color: "var(--muted)" }}>…</div>
          ) : currentList.length === 0 ? (
            <div className="text-center py-12 font-display italic text-xl"
                 style={{ color: "var(--muted)" }} data-testid={`galerie-empty-${tab}`}>
              {tab === "photos" ? t.emptyPhotos : t.emptyVideos}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4"
                 data-testid={`galerie-grid-${tab}`}>
              {tab === "photos"
                ? visible.map((p, i) => <PhotoThumb key={p.id} p={p} locale={locale} onOpen={() => openPhoto(i)} />)
                : visible.map((v, i) => <VideoThumb key={v.id} v={v} locale={locale} onOpen={() => openVideo(i)} />)}
            </div>
          )}

          {hasMore && (
            <div className="text-center pt-10" data-testid="galerie-load-more-wrap">
              <button
                data-testid="galerie-load-more"
                onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
                className="px-6 py-3 border font-caps text-[0.7rem]"
                style={{
                  borderColor: "var(--ink)", color: "var(--ink)",
                  letterSpacing: "0.22em", background: "transparent",
                }}
              >
                {t.loadMore}
                <span className="ml-2" style={{ color: "var(--muted)" }}>
                  ({visible.length} / {currentList.length})
                </span>
              </button>
            </div>
          )}
        </div>
      </section>

      {lightbox !== null && (() => {
        const list = lightbox.kind === "photo" ? photos : videos;
        const item = list[lightbox.index];
        if (!item) return null;
        return (
          <Lightbox
            kind={lightbox.kind}
            item={item}
            locale={locale}
            closeLabel={t.close}
            onClose={close}
            hasPrev={lightbox.index > 0}
            hasNext={lightbox.index < list.length - 1}
            onPrev={() => setLightbox({ kind: lightbox.kind, index: lightbox.index - 1 })}
            onNext={() => setLightbox({ kind: lightbox.kind, index: lightbox.index + 1 })}
          />
        );
      })()}
    </div>
  );
}

// ============================== Sub-components ==============================
function TabButton({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="px-5 py-2 font-caps text-[0.7rem] border transition-colors"
      style={{
        letterSpacing: "0.22em",
        borderColor: active ? "var(--ink)" : "var(--border)",
        background: active ? "var(--ink)" : "transparent",
        color: active ? "#fff" : "var(--ink)",
        fontWeight: active ? 600 : 400,
      }}
    >
      {children}
    </button>
  );
}

function PhotoThumb({ p, locale, onOpen }) {
  const cap = pickLocalized(p.caption, locale);
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`galerie-thumb-${p.id}`}
      className="group relative block bg-white border overflow-hidden"
      style={{ borderColor: "var(--border)", aspectRatio: "4/3" }}
    >
      <img
        src={resolveAsset(p.image_url)}
        alt={cap || ""}
        loading="lazy"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
      />
      {cap && (
        <div
          className="absolute inset-x-0 bottom-0 px-3 py-2 font-body text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ background: "linear-gradient(to top, rgba(20,16,12,0.85), transparent)" }}
        >
          {cap}
        </div>
      )}
    </button>
  );
}

function VideoThumb({ v, locale, onOpen }) {
  const cap = pickLocalized(v.caption, locale);
  const poster = v.thumbnail_url ? resolveAsset(v.thumbnail_url) : "";
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`galerie-video-thumb-${v.id}`}
      className="group relative block border overflow-hidden"
      style={{ borderColor: "var(--border)", aspectRatio: "16/9", background: "var(--ink)" }}
    >
      {poster ? (
        <img
          src={poster}
          alt={cap || ""}
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center"
             style={{ background: "linear-gradient(135deg, #2a221a 0%, #14100c 100%)" }}>
          <span className="font-display italic text-white/40 text-sm px-4 text-center">{cap || "Vidéo"}</span>
        </div>
      )}
      <div className="absolute inset-0 flex items-center justify-center transition-colors"
           style={{ background: "rgba(0,0,0,0.18)" }}>
        <div className="w-16 h-16 rounded-full flex items-center justify-center transition-transform group-hover:scale-110"
             style={{ background: "rgba(255,255,255,0.92)", boxShadow: "0 6px 24px rgba(0,0,0,0.4)" }}>
          <span style={{ marginLeft: 4, color: "var(--ink)", fontSize: 22 }}>▶</span>
        </div>
      </div>
      {cap && (
        <div
          className="absolute inset-x-0 bottom-0 px-3 py-2 font-body text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ background: "linear-gradient(to top, rgba(20,16,12,0.92), transparent)" }}
        >
          {cap}
        </div>
      )}
    </button>
  );
}

function Lightbox({ kind, item, locale, closeLabel, onClose, hasPrev, hasNext, onPrev, onNext }) {
  const cap = pickLocalized(item.caption, locale);
  return (
    <div
      data-testid="galerie-lightbox"
      onClick={onClose}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: "rgba(20,16,12,0.94)" }}
    >
      <button type="button" onClick={(e) => { e.stopPropagation(); onClose(); }}
              aria-label={closeLabel} data-testid="galerie-lightbox-close"
              className="absolute top-5 right-5 w-10 h-10 flex items-center justify-center text-white"
              style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.3)" }}>
        ✕
      </button>

      {hasPrev && (
        <button type="button" onClick={(e) => { e.stopPropagation(); onPrev(); }}
                aria-label="Précédent" data-testid="galerie-lightbox-prev"
                className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center text-white text-2xl"
                style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.3)" }}>
          ‹
        </button>
      )}

      {hasNext && (
        <button type="button" onClick={(e) => { e.stopPropagation(); onNext(); }}
                aria-label="Suivant" data-testid="galerie-lightbox-next"
                className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center text-white text-2xl"
                style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.3)" }}>
          ›
        </button>
      )}

      <figure onClick={(e) => e.stopPropagation()} className="max-w-6xl max-h-[88vh] flex flex-col items-center gap-4">
        {kind === "photo" ? (
          <img src={resolveAsset(item.image_url)} alt={cap || ""}
               className="max-h-[78vh] max-w-full object-contain"
               style={{ boxShadow: "0 20px 60px rgba(0,0,0,0.6)" }} />
        ) : (
          <VideoPlayer video={item} />
        )}
        {cap && (
          <figcaption className="font-display italic text-lg text-white/90 text-center">{cap}</figcaption>
        )}
      </figure>
    </div>
  );
}

function VideoPlayer({ video }) {
  const provider = video.provider || "upload";
  const baseStyle = {
    width: "min(1100px, 90vw)",
    aspectRatio: "16/9",
    boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
    background: "black",
  };
  if (provider === "youtube" && video.embed_id) {
    return (
      <iframe
        data-testid="galerie-video-player"
        src={`https://www.youtube-nocookie.com/embed/${video.embed_id}?autoplay=1&rel=0`}
        title="Vidéo"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        style={baseStyle}
        frameBorder="0"
      />
    );
  }
  if (provider === "vimeo" && video.embed_id) {
    return (
      <iframe
        data-testid="galerie-video-player"
        src={`https://player.vimeo.com/video/${video.embed_id}?autoplay=1`}
        title="Vidéo"
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
        style={baseStyle}
        frameBorder="0"
      />
    );
  }
  // Native HTML5 video for uploads
  return (
    <video
      data-testid="galerie-video-player"
      src={resolveAsset(video.url)}
      controls
      autoPlay
      playsInline
      poster={video.thumbnail_url ? resolveAsset(video.thumbnail_url) : undefined}
      style={baseStyle}
    />
  );
}
